import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RgbaStringColorPicker } from "react-colorful";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeDropper } from "@fortawesome/free-solid-svg-icons";
import useOuterClick from "../hooks/useOuterClick.js";

const normalizeEyeDropperColor = (hexColor) => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hexColor || "");
  if (!match) return hexColor;

  const value = match[1];
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;

  if (luminance <= 45) return "rgba(0, 0, 0, 1)";
  if (luminance >= 245) return "rgba(255, 255, 255, 1)";

  return `rgba(${r}, ${g}, ${b}, 1)`;
};

const PopoverPicker = ({ color, onChangeX, classes }) => {
  const popover = useRef();
  const swatchRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [isPickingColor, setIsPickingColor] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({
    top: 0,
    left: 0,
    visibility: "hidden",
  });
  const canUseEyeDropper =
    typeof window !== "undefined" && typeof window.EyeDropper !== "undefined";

  const isColor = (strColor) => {
    const s = new Option().style;
    s.color = strColor;
    return s.color !== "";
  };

  const innerRef = useOuterClick((event) => {
    if (popover.current?.contains(event.target)) {
      return;
    }
    setIsOpen(false);
  });

  const updatePopoverPosition = () => {
    const swatch = swatchRef.current;
    const popoverElement = popover.current;
    if (!swatch || !popoverElement) return;

    const gap = 4;
    const viewportMargin = 8;
    const swatchRect = swatch.getBoundingClientRect();
    const popoverRect = popoverElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - swatchRect.bottom;
    const spaceAbove = swatchRect.top;
    const shouldOpenAbove =
      spaceBelow < popoverRect.height + gap && spaceAbove > spaceBelow;

    const preferredTop = shouldOpenAbove
      ? swatchRect.top - popoverRect.height - gap
      : swatchRect.bottom + gap;
    const preferredLeft = swatchRect.left;
    const maxTop = Math.max(
      viewportMargin,
      viewportHeight - popoverRect.height - viewportMargin,
    );
    const maxLeft = Math.max(
      viewportMargin,
      viewportWidth - popoverRect.width - viewportMargin,
    );

    setPopoverPosition({
      top: Math.min(Math.max(preferredTop, viewportMargin), maxTop),
      left: Math.min(Math.max(preferredLeft, viewportMargin), maxLeft),
      visibility: "visible",
    });
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    updatePopoverPosition();
    window.addEventListener("resize", updatePopoverPosition);
    window.addEventListener("scroll", updatePopoverPosition, true);

    return () => {
      window.removeEventListener("resize", updatePopoverPosition);
      window.removeEventListener("scroll", updatePopoverPosition, true);
    };
  }, [isOpen]);

  const openPicker = () => {
    setPopoverPosition({ top: 0, left: 0, visibility: "hidden" });
    setIsOpen(true);
  };

  const handleEyeDropper = async () => {
    if (!canUseEyeDropper || isPickingColor) return;

    setIsPickingColor(true);
    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      if (result?.sRGBHex) {
        onChangeX(normalizeEyeDropperColor(result.sRGBHex));
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Error using eye dropper:", error);
      }
    } finally {
      setIsPickingColor(false);
    }
  };

  return (
    <div className={classes} ref={innerRef}>
      <div
        ref={swatchRef}
        className="swatch"
        style={{ background: color }}
        onClick={openPicker}
      />

      {isOpen && typeof document !== "undefined" && createPortal((
        <div
          className="popover"
          ref={popover}
          style={{
            position: "fixed",
            top: popoverPosition.top,
            left: popoverPosition.left,
            visibility: popoverPosition.visibility,
            zIndex: 2000,
          }}
        >
          <RgbaStringColorPicker
            color={isColor(color) ? color : ""}
            onChange={onChangeX}
          />
          <div className="d-flex">
            <button
              type="button"
              className="btn btn-light btn-sm border-0"
              style={{
                borderRadius: "0 0 0 8px",
                minWidth: "38px",
              }}
              disabled={!canUseEyeDropper || isPickingColor}
              onClick={handleEyeDropper}
              title={
                canUseEyeDropper
                  ? "Selecionar cor da tela"
                  : "Conta-gotas indisponível neste navegador"
              }
            >
              <FontAwesomeIcon icon={faEyeDropper} />
            </button>
            <input
              type="text"
              className="w-100 text-center"
              value={color}
              onChange={(e) => {
                onChangeX(e.target.value);
              }}
            />
          </div>
        </div>
      ), document.body)}
    </div>
  );
};

export default PopoverPicker;
